<script setup lang="ts">
// Public marketing landing — the page Google indexes and the first thing a
// logged-out visitor sees. Statically prerendered at build time (see
// scripts/prerender.mjs) so the content is in the HTML before any JS runs.
//
// Everything here is hand-rolled semantic HTML + scoped CSS rather than
// Vuetify components: a marketing page wants full control over typography,
// motion and layout, and clean headings for SEO.
const year = 2026;

const features = [
  {
    eyebrow: 'Rooster',
    title: 'Je hele week, in één kleurrijk overzicht',
    body: 'Zet je toetsen, huiswerk, sport en afspraken in je rooster. Elke categorie krijgt een eigen kleur, zodat je in één oogopslag ziet wat er die dag op je staat te wachten.',
    points: ['Week- en lijstweergave', 'Eigen categorieën met kleuren', 'Alles op één plek'],
    img: '/marketing/screens/rooster.png',
    alt: 'Het weekrooster van LockIn met gekleurde lesblokken en huiswerk',
    accent: 'blue',
  },
  {
    eyebrow: 'Focus-timer',
    title: 'Een timer die alleen telt als jij écht werkt',
    body: 'Start de timer bij je huiswerk en hij loopt mee terwijl je bezig bent. Elk half uur vraagt hij of je er nog bent — druk je niet op tijd, dan pauzeert hij vanzelf. Zo tellen alleen de minuten waarin je echt studeert.',
    points: ['Check-in elk half uur', 'Pauzeert automatisch', 'Eerlijke studietijd'],
    img: '/marketing/screens/event.png',
    alt: 'Een huiswerk-event in LockIn met de focus-timer en de knop Start timer',
    accent: 'teal',
  },
  {
    eyebrow: 'LIA · slimme planner',
    title: 'Laat LIA je leerwerk voor je inplannen',
    body: 'Vertel LIA wat je moet leren, hoeveel uur je nodig denkt te hebben en wanneer de toets is. LIA kijkt naar je agenda en verdeelt de stof over losse leersessies. Eén tik en ze staan in je rooster.',
    points: ['Kies: snel klaar, gestaag of last-minute', 'Houdt rekening met je agenda', 'In één tik geaccepteerd'],
    img: '/marketing/screens/lia.png',
    alt: 'De LIA-studieplanner van LockIn met leerprofielen en een urenslider',
    accent: 'purple',
  },
  {
    eyebrow: 'Punten, levels & badges',
    title: 'Studeren dat eindelijk iets oplevert',
    body: 'Voor elk uur dat je echt studeert krijg je punten. Punten worden levels, levels worden badges. Je ziet je eigen vooruitgang groeien — een reden om vandaag te beginnen, en morgen weer.',
    points: ['Punten voor echte studietijd', 'Levels en streaks', 'Badges om te verzamelen'],
    img: '/marketing/screens/profiel.png',
    alt: 'Het profiel in LockIn met level, voortgangsbalk en verdiende badges',
    accent: 'amber',
  },
  {
    eyebrow: 'Feed',
    title: 'Samen leren werkt aanstekelijk',
    body: 'In de Feed zie je waar klasgenoten mee bezig zijn: wie er net een sessie afrondde, wie een level omhoog ging. Niet om te concurreren — wel om elkaar een beetje aan te steken.',
    points: ['Zie de voortgang van anderen', 'Vier elkaars successen', 'Motivatie zonder druk'],
    img: '/marketing/screens/feed.png',
    alt: 'De activiteitenfeed van LockIn met voltooide sessies en level-ups van klasgenoten',
    accent: 'blue',
  },
];

const steps = [
  { n: '1', title: 'Maak een gratis account', body: 'Kies je klas en stel in hoeveel uur je per dag wilt studeren. Klaar in een minuut.' },
  { n: '2', title: 'Vul je rooster', body: 'Zet je huiswerk en toetsen erin — of laat LIA je leerwerk automatisch inplannen.' },
  { n: '3', title: 'Start de timer en studeer', body: 'Werk met focus, verdien punten en zie je levels en badges groeien.' },
];

const parents = [
  { icon: 'school', title: 'Geen schoolsysteem nodig', body: 'LockIn werkt helemaal los van Magister of een ander schoolsysteem. Een account is in een minuut gemaakt.' },
  { icon: 'shield', title: 'Alleen wat nodig is', body: 'We vragen alleen de gegevens die de app nodig heeft om te werken. Geen advertenties, geen ruis.' },
  { icon: 'gift', title: 'Gratis te gebruiken', body: 'LockIn is gratis. Je installeert het zo via de browser, net als een gewone app.' },
  { icon: 'flag', title: 'In het Nederlands', body: 'Gemaakt voor Nederlandse scholieren — alle uitleg en knoppen zijn in het Nederlands.' },
];
</script>

<template>
  <div class="lkn">
    <!-- ───────────────────────── Header ───────────────────────── -->
    <header class="lkn-header">
      <a href="#top" class="lkn-logo" aria-label="LockIn home">
        <svg class="lkn-logo__lock" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 10V7a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
          <rect x="4" y="10" width="16" height="11" rx="3.2" fill="currentColor" />
          <circle cx="12" cy="14.6" r="1.9" fill="#fff" />
          <rect x="11.1" y="15" width="1.8" height="3.4" rx="0.9" fill="#fff" />
        </svg>
        <span class="lkn-logo__word">LockIn<span class="lkn-logo__dot">.</span></span>
      </a>

      <nav class="lkn-nav" aria-label="Hoofdmenu">
        <a href="#functies">Functies</a>
        <a href="#hoe">Hoe het werkt</a>
        <a href="#ouders">Voor ouders</a>
      </nav>

      <div class="lkn-header__cta">
        <RouterLink to="/login" class="lkn-btn lkn-btn--ghost">Inloggen</RouterLink>
        <RouterLink to="/register" class="lkn-btn lkn-btn--solid">Gratis starten</RouterLink>
      </div>
    </header>

    <main id="top">
      <!-- ───────────────────────── Hero ───────────────────────── -->
      <section class="lkn-hero">
        <div class="lkn-hero__grid" aria-hidden="true"></div>
        <div class="lkn-hero__glow" aria-hidden="true"></div>

        <div class="lkn-hero__copy">
          <span class="lkn-eyebrow">
            <svg viewBox="0 0 24 24" aria-hidden="true" class="lkn-eyebrow__lock">
              <path d="M7 10V7a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
              <rect x="4" y="10" width="16" height="11" rx="3.2" fill="currentColor" />
            </svg>
            Gratis studieplanner voor scholieren
          </span>

          <h1 class="lkn-hero__title">
            <span class="r1">Plan slim.</span>
            <span class="r2">Studeer met <em>focus</em>.</span>
            <span class="r3">Verdien punten.</span>
          </h1>

          <p class="lkn-hero__lead">
            LockIn helpt je je huiswerk en toetsen te plannen, het ook
            <strong>écht te doen</strong>, en daar punten en levels voor terug te
            krijgen. Je rooster, een slimme planner en een eerlijke timer —
            alles in één app op je telefoon.
          </p>

          <div class="lkn-hero__actions">
            <RouterLink to="/register" class="lkn-btn lkn-btn--solid lkn-btn--lg">
              Gratis account maken
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </RouterLink>
            <RouterLink to="/login" class="lkn-btn lkn-btn--outline lkn-btn--lg">Inloggen</RouterLink>
          </div>

          <ul class="lkn-trust">
            <li>Geen schoolsysteem nodig</li>
            <li>Telefoon, tablet &amp; laptop</li>
            <li>Helemaal in het Nederlands</li>
          </ul>
        </div>

        <div class="lkn-hero__visual">
          <div class="lkn-phone lkn-phone--hero">
            <div class="lkn-phone__notch" aria-hidden="true"></div>
            <img src="/marketing/screens/rooster.png" width="390" height="844"
                 alt="Het weekrooster van LockIn met gekleurde lesblokken" loading="eager" />
          </div>

          <div class="lkn-chip lkn-chip--points" aria-hidden="true">
            <span class="lkn-chip__icon">★</span>
            <span><b>+1 punt</b><br />goed bezig!</span>
          </div>
          <div class="lkn-chip lkn-chip--streak" aria-hidden="true">
            <span class="lkn-chip__icon">🔥</span>
            <span><b>Level 6</b><br />Lvl-up!</span>
          </div>
        </div>
      </section>

      <!-- ───────────────── Functies (alternating) ───────────────── -->
      <section id="functies" class="lkn-section">
        <div class="lkn-section__head">
          <span class="lkn-kicker">Wat je krijgt</span>
          <h2>Alles wat je nodig hebt om te beginnen<br />— en te blíjven beginnen</h2>
        </div>

        <div class="lkn-features">
          <article v-for="(f, i) in features" :key="f.title"
                   class="lkn-feature" :class="[{ 'is-flip': i % 2 === 1 }, 'accent-' + f.accent]">
            <div class="lkn-feature__text">
              <span class="lkn-feature__eyebrow">{{ f.eyebrow }}</span>
              <h3>{{ f.title }}</h3>
              <p>{{ f.body }}</p>
              <ul class="lkn-checks">
                <li v-for="p in f.points" :key="p">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ p }}
                </li>
              </ul>
            </div>
            <div class="lkn-feature__visual">
              <div class="lkn-phone">
                <div class="lkn-phone__notch" aria-hidden="true"></div>
                <img :src="f.img" :alt="f.alt" width="390" height="844" loading="lazy" />
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- ───────────────────── Hoe het werkt ───────────────────── -->
      <section id="hoe" class="lkn-steps">
        <div class="lkn-section__head lkn-section__head--light">
          <span class="lkn-kicker lkn-kicker--light">In drie stappen</span>
          <h2>Zo werkt het</h2>
        </div>
        <ol class="lkn-steps__list">
          <li v-for="s in steps" :key="s.n" class="lkn-step">
            <span class="lkn-step__n">{{ s.n }}</span>
            <h3>{{ s.title }}</h3>
            <p>{{ s.body }}</p>
          </li>
        </ol>
        <div class="lkn-steps__cta">
          <RouterLink to="/register" class="lkn-btn lkn-btn--solid lkn-btn--lg">Begin nu — het is gratis</RouterLink>
        </div>
      </section>

      <!-- ────────────────────── Over LockIn ────────────────────── -->
      <section id="over" class="lkn-about">
        <div class="lkn-about__inner">
          <span class="lkn-kicker">Over LockIn</span>
          <h2>Het moeilijkste is beginnen.<br />Daar helpt LockIn je mee.</h2>
          <p>
            De meeste leerlingen lopen niet vast omdat ze de stof niet snappen,
            maar omdat ze niet weten waar ze moeten beginnen, of het blijven
            uitstellen. LIA maakt van een vaag <em>“ik moet nog leren voor
            wiskunde”</em> een duidelijk plan. De timer zorgt dat je je focus
            volhoudt en niet stiekem op TikTok eindigt. En de punten en levels
            geven je iets om naar toe te werken.
          </p>
          <p>
            LockIn vervangt geen docent en geen samenvatting. Wat de app wél
            doet, is het makkelijker maken om vandaag te beginnen — en daarna
            morgen weer. En dat is meestal precies het stukje waar het op
            vastliep.
          </p>
        </div>
      </section>

      <!-- ─────────────────── Voor ouders & docenten ─────────────── -->
      <section id="ouders" class="lkn-section lkn-section--soft">
        <div class="lkn-section__head">
          <span class="lkn-kicker">Voor ouders &amp; docenten</span>
          <h2>Veilig, gratis en zonder gedoe</h2>
        </div>
        <div class="lkn-parents">
          <div v-for="p in parents" :key="p.title" class="lkn-parent">
            <span class="lkn-parent__icon" :data-icon="p.icon" aria-hidden="true">
              <svg v-if="p.icon === 'school'" viewBox="0 0 24 24"><path d="M3 9l9-5 9 5-9 5-9-5z M7 11.5V16c0 1.2 2.2 2.5 5 2.5s5-1.3 5-2.5v-4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <svg v-else-if="p.icon === 'shield'" viewBox="0 0 24 24"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z M9 12l2 2 4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <svg v-else-if="p.icon === 'gift'" viewBox="0 0 24 24"><path d="M4 11h16v9H4z M4 7h16v4H4z M12 7v13 M12 7S10 3 7.5 4.5 9.5 7 12 7z M12 7s2-4 4.5-2.5S14.5 7 12 7z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <svg v-else viewBox="0 0 24 24"><path d="M5 21V4 M5 4h11l-2 3 2 3H5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
            <h3>{{ p.title }}</h3>
            <p>{{ p.body }}</p>
          </div>
        </div>
      </section>

      <!-- ───────────────────────── CTA band ─────────────────────── -->
      <section class="lkn-cta">
        <div class="lkn-cta__inner">
          <svg class="lkn-cta__lock" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 10V7a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
            <rect x="4" y="10" width="16" height="11" rx="3.2" fill="currentColor" />
            <circle cx="12" cy="14.6" r="1.9" fill="#0E2A3D" />
            <rect x="11.1" y="15" width="1.8" height="3.4" rx="0.9" fill="#0E2A3D" />
          </svg>
          <h2>Klaar om in te loggen op je focus?</h2>
          <p>Maak een gratis account en plan je eerste leersessie binnen een minuut.</p>
          <div class="lkn-cta__actions">
            <RouterLink to="/register" class="lkn-btn lkn-btn--solid lkn-btn--lg">Gratis account maken</RouterLink>
            <RouterLink to="/login" class="lkn-btn lkn-btn--lightghost lkn-btn--lg">Ik heb al een account</RouterLink>
          </div>
        </div>
      </section>
    </main>

    <!-- ───────────────────────── Footer ───────────────────────── -->
    <footer class="lkn-footer">
      <div class="lkn-footer__top">
        <div class="lkn-footer__brand">
          <span class="lkn-logo lkn-logo--footer">
            <svg class="lkn-logo__lock" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10V7a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
              <rect x="4" y="10" width="16" height="11" rx="3.2" fill="currentColor" />
              <circle cx="12" cy="14.6" r="1.9" fill="#fff" />
              <rect x="11.1" y="15" width="1.8" height="3.4" rx="0.9" fill="#fff" />
            </svg>
            <span class="lkn-logo__word">LockIn<span class="lkn-logo__dot">.</span></span>
          </span>
          <p>Studieplanner voor scholieren.<br />Focus, planning en plezier in studeren.</p>
        </div>
        <nav class="lkn-footer__cols" aria-label="Footer">
          <div>
            <h4>Aan de slag</h4>
            <RouterLink to="/register">Account maken</RouterLink>
            <RouterLink to="/login">Inloggen</RouterLink>
            <a href="#functies">Functies</a>
          </div>
          <div>
            <h4>Meer</h4>
            <a href="#hoe">Hoe het werkt</a>
            <a href="#over">Over LockIn</a>
            <a href="#ouders">Voor ouders</a>
          </div>
        </nav>
      </div>
      <div class="lkn-footer__bottom">
        <span>© {{ year }} LockIn</span>
        <span>Studieplanner voor scholieren — in het Nederlands.</span>
      </div>
    </footer>
  </div>
</template>

<style>
/* Self-hosted fonts (no third-party CDN at runtime — privacy-friendly for a
   site aimed at minors). Files live in /public/fonts. */
@font-face {
  font-family: 'Bricolage Grotesque';
  font-style: normal;
  font-weight: 400 800;
  font-display: swap;
  src: url('/fonts/bricolage-latin.woff2') format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
@font-face {
  font-family: 'Bricolage Grotesque';
  font-style: normal;
  font-weight: 400 800;
  font-display: swap;
  src: url('/fonts/bricolage-latinext.woff2') format('woff2');
  unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
}
@font-face {
  font-family: 'Figtree';
  font-style: normal;
  font-weight: 300 800;
  font-display: swap;
  src: url('/fonts/figtree-latin.woff2') format('woff2');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
@font-face {
  font-family: 'Figtree';
  font-style: normal;
  font-weight: 300 800;
  font-display: swap;
  src: url('/fonts/figtree-latinext.woff2') format('woff2');
  unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
}
</style>

<style scoped>
.lkn {
  /* Brand tokens (from the logo / theme.md) */
  --blue:   #1FA8F2;
  --blue-d: #0E7DBE;
  --teal:   #3BC4B5;
  --teal-d: #249E91;
  --bg:     #D7EEFB;
  --ink:    #0F1F2E;
  --muted:  #5B6B7B;
  --line:   #E2EEF8;
  --soft:   #F1F8FE;
  --purple: #8B5CF6;
  --amber:  #F59E0B;

  --display: 'Bricolage Grotesque', 'Segoe UI', system-ui, sans-serif;
  --body: 'Figtree', 'Segoe UI', system-ui, sans-serif;

  --maxw: 1140px;
  --radius: 22px;

  font-family: var(--body);
  color: var(--ink);
  background: #fff;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  overflow-x: clip;
}
.lkn * { box-sizing: border-box; }
.lkn :where(h1, h2, h3, h4) { font-family: var(--display); line-height: 1.05; letter-spacing: -0.02em; margin: 0; font-weight: 800; }
.lkn p { margin: 0; }
.lkn a { color: inherit; text-decoration: none; }

/* ── Buttons ────────────────────────────────────────────── */
.lkn-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font-family: var(--display); font-weight: 700; font-size: 15px;
  padding: 11px 18px; border-radius: 999px; cursor: pointer; border: 2px solid transparent;
  transition: transform .15s ease, box-shadow .2s ease, background .2s ease, color .2s ease;
  white-space: nowrap;
}
.lkn-btn svg { width: 18px; height: 18px; }
.lkn-btn--lg { font-size: 17px; padding: 15px 26px; }
.lkn-btn--solid { background: var(--blue); color: #fff; box-shadow: 0 8px 20px -8px rgba(31,168,242,.7); }
.lkn-btn--solid:hover { background: var(--blue-d); transform: translateY(-2px); box-shadow: 0 14px 26px -10px rgba(31,168,242,.8); }
.lkn-btn--ghost { color: var(--ink); }
.lkn-btn--ghost:hover { color: var(--blue); }
.lkn-btn--outline { background: #fff; color: var(--ink); border-color: var(--line); }
.lkn-btn--outline:hover { border-color: var(--blue); color: var(--blue); transform: translateY(-2px); }
.lkn-btn--lightghost { background: rgba(255,255,255,.12); color: #fff; border-color: rgba(255,255,255,.4); }
.lkn-btn--lightghost:hover { background: rgba(255,255,255,.22); transform: translateY(-2px); }

/* ── Logo ───────────────────────────────────────────────── */
.lkn-logo { display: inline-flex; align-items: center; gap: 8px; }
.lkn-logo__lock { width: 26px; height: 26px; color: var(--teal); flex: none; }
.lkn-logo__word { font-family: var(--display); font-weight: 800; font-size: 23px; letter-spacing: -0.03em; color: var(--blue); }
.lkn-logo__dot { color: var(--teal); }
.lkn-logo--footer .lkn-logo__word { color: #fff; }

/* ── Header ─────────────────────────────────────────────── */
.lkn-header {
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; gap: 24px;
  padding: 14px clamp(16px, 4vw, 40px);
  background: rgba(255,255,255,.82);
  backdrop-filter: saturate(1.4) blur(14px);
  border-bottom: 1px solid var(--line);
}
.lkn-nav { display: flex; gap: 26px; margin-left: 14px; font-weight: 600; color: var(--muted); }
.lkn-nav a { position: relative; padding: 4px 0; transition: color .2s; }
.lkn-nav a:hover { color: var(--ink); }
.lkn-nav a::after { content: ''; position: absolute; left: 0; bottom: -2px; width: 0; height: 2px; background: var(--blue); border-radius: 2px; transition: width .25s ease; }
.lkn-nav a:hover::after { width: 100%; }
.lkn-header__cta { margin-left: auto; display: flex; align-items: center; gap: 10px; }

/* ── Hero ───────────────────────────────────────────────── */
.lkn-hero {
  position: relative; overflow: clip;
  display: grid; grid-template-columns: 1.05fr .95fr; gap: clamp(24px, 5vw, 70px); align-items: center;
  max-width: var(--maxw); margin: 0 auto;
  padding: clamp(40px, 7vw, 92px) clamp(16px, 4vw, 40px) clamp(60px, 8vw, 110px);
}
.lkn-hero__grid {
  position: absolute; inset: -10% -10% auto; height: 120%; z-index: 0; pointer-events: none;
  background-image:
    linear-gradient(to right, rgba(31,168,242,.07) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(31,168,242,.07) 1px, transparent 1px);
  background-size: 44px 44px;
  -webkit-mask-image: radial-gradient(120% 80% at 70% 18%, #000 0%, transparent 70%);
  mask-image: radial-gradient(120% 80% at 70% 18%, #000 0%, transparent 70%);
}
.lkn-hero__glow {
  position: absolute; z-index: 0; pointer-events: none;
  width: 60vw; height: 60vw; max-width: 760px; max-height: 760px;
  right: -12%; top: -22%;
  background: radial-gradient(closest-side, rgba(59,196,181,.30), rgba(31,168,242,.16) 55%, transparent 75%);
  filter: blur(8px);
}
.lkn-hero__copy { position: relative; z-index: 1; }
.lkn-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  background: #fff; border: 1px solid var(--line); color: var(--teal-d);
  font-weight: 700; font-size: 13.5px; padding: 7px 14px 7px 11px; border-radius: 999px;
  box-shadow: 0 6px 18px -12px rgba(15,31,46,.4);
}
.lkn-eyebrow__lock { width: 16px; height: 16px; color: var(--teal); }
.lkn-hero__title { font-size: clamp(40px, 6.4vw, 74px); margin: 20px 0 0; }
.lkn-hero__title span { display: block; }
.lkn-hero__title em { font-style: normal; color: var(--blue); position: relative; }
.lkn-hero__title em::after {
  content: ''; position: absolute; left: -2px; right: -2px; bottom: .06em; height: .14em;
  background: var(--teal); border-radius: 4px; transform: scaleX(0); transform-origin: left;
  animation: underline 0.7s cubic-bezier(.2,.7,.2,1) 0.7s forwards;
}
.lkn-hero__lead { margin-top: 22px; font-size: clamp(16px, 1.6vw, 19px); color: var(--muted); max-width: 30em; }
.lkn-hero__lead strong { color: var(--ink); }
.lkn-hero__actions { margin-top: 30px; display: flex; flex-wrap: wrap; gap: 12px; }
.lkn-trust { list-style: none; padding: 0; margin: 26px 0 0; display: flex; flex-wrap: wrap; gap: 8px 18px; color: var(--muted); font-size: 14px; font-weight: 600; }
.lkn-trust li { display: flex; align-items: center; gap: 7px; }
.lkn-trust li::before { content: ''; width: 16px; height: 16px; flex: none; border-radius: 50%; background: var(--teal); -webkit-mask: center/12px no-repeat url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='none' stroke='%23fff' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' d='M5 13l4 4L19 7'/%3E%3C/svg%3E"); mask: center/12px no-repeat url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='none' stroke='%23fff' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' d='M5 13l4 4L19 7'/%3E%3C/svg%3E"); }

/* ── Phone frames ───────────────────────────────────────── */
.lkn-phone {
  position: relative; width: 100%; max-width: 296px; margin: 0 auto;
  background: #0F1F2E; border-radius: 42px; padding: 11px;
  box-shadow: 0 40px 80px -34px rgba(15,31,46,.55), 0 8px 24px -16px rgba(15,31,46,.4);
  border: 1px solid rgba(255,255,255,.12);
}
.lkn-phone img { display: block; width: 100%; height: auto; border-radius: 32px; }
.lkn-phone__notch { position: absolute; top: 11px; left: 50%; transform: translateX(-50%); width: 38%; height: 22px; background: #0F1F2E; border-radius: 0 0 14px 14px; z-index: 2; }
.lkn-hero__visual { position: relative; z-index: 1; }
.lkn-phone--hero { transform: rotate(2.5deg); }

.lkn-chip {
  position: absolute; z-index: 3; display: flex; align-items: center; gap: 10px;
  background: #fff; border-radius: 16px; padding: 11px 15px; font-size: 13px; line-height: 1.25;
  box-shadow: 0 18px 36px -16px rgba(15,31,46,.5); font-weight: 600;
  animation: float 5s ease-in-out infinite;
}
.lkn-chip b { font-family: var(--display); font-size: 15px; }
.lkn-chip__icon { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 10px; font-size: 16px; }
.lkn-chip--points { left: -6%; top: 24%; }
.lkn-chip--points .lkn-chip__icon { background: rgba(31,168,242,.15); color: var(--blue); }
.lkn-chip--streak { right: -6%; bottom: 16%; animation-delay: -2.5s; }
.lkn-chip--streak .lkn-chip__icon { background: rgba(245,158,11,.16); }

/* ── Generic section heads ──────────────────────────────── */
.lkn-section { max-width: var(--maxw); margin: 0 auto; padding: clamp(56px, 8vw, 110px) clamp(16px, 4vw, 40px); }
.lkn-section--soft { max-width: none; background: var(--soft); }
.lkn-section--soft > * { max-width: var(--maxw); margin-inline: auto; }
.lkn-section__head { text-align: center; max-width: 760px; margin: 0 auto clamp(36px, 5vw, 64px); }
.lkn-section__head h2 { font-size: clamp(28px, 4vw, 46px); }
.lkn-kicker { display: inline-block; font-family: var(--display); font-weight: 700; font-size: 13px; letter-spacing: .12em; text-transform: uppercase; color: var(--blue); margin-bottom: 14px; }
.lkn-kicker--light { color: var(--teal); }

/* ── Features ───────────────────────────────────────────── */
.lkn-features { display: flex; flex-direction: column; gap: clamp(48px, 8vw, 104px); }
.lkn-feature { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(28px, 5vw, 76px); align-items: center; }
.lkn-feature.is-flip .lkn-feature__text { order: 2; }
.lkn-feature__eyebrow { font-family: var(--display); font-weight: 700; font-size: 13px; letter-spacing: .1em; text-transform: uppercase; color: var(--blue); }
.accent-teal .lkn-feature__eyebrow { color: var(--teal-d); }
.accent-purple .lkn-feature__eyebrow { color: var(--purple); }
.accent-amber .lkn-feature__eyebrow { color: var(--amber); }
.lkn-feature__text h3 { font-size: clamp(24px, 3.2vw, 36px); margin: 12px 0 16px; }
.lkn-feature__text p { color: var(--muted); font-size: 17px; max-width: 34em; }
.lkn-checks { list-style: none; padding: 0; margin: 22px 0 0; display: grid; gap: 12px; }
.lkn-checks li { display: flex; align-items: center; gap: 11px; font-weight: 600; }
.lkn-checks svg { width: 22px; height: 22px; flex: none; padding: 4px; border-radius: 8px; background: rgba(31,168,242,.12); color: var(--blue); }
.accent-teal .lkn-checks svg { background: rgba(59,196,181,.16); color: var(--teal-d); }
.accent-purple .lkn-checks svg { background: rgba(139,92,246,.14); color: var(--purple); }
.accent-amber .lkn-checks svg { background: rgba(245,158,11,.16); color: var(--amber); }

/* Soft tinted glow behind each feature phone, themed per accent */
.lkn-feature__visual { position: relative; }
.lkn-feature__visual::before {
  content: ''; position: absolute; inset: 4% 14%; z-index: 0; border-radius: 50%;
  background: radial-gradient(closest-side, rgba(31,168,242,.22), transparent 72%); filter: blur(10px);
}
.accent-teal .lkn-feature__visual::before { background: radial-gradient(closest-side, rgba(59,196,181,.26), transparent 72%); }
.accent-purple .lkn-feature__visual::before { background: radial-gradient(closest-side, rgba(139,92,246,.22), transparent 72%); }
.accent-amber .lkn-feature__visual::before { background: radial-gradient(closest-side, rgba(245,158,11,.24), transparent 72%); }
.lkn-feature__visual .lkn-phone { position: relative; z-index: 1; max-width: 270px; }
.lkn-feature:not(.is-flip) .lkn-phone { transform: rotate(-2.5deg); }
.lkn-feature.is-flip .lkn-phone { transform: rotate(2.5deg); }

/* ── Steps ──────────────────────────────────────────────── */
.lkn-steps { background: var(--ink); color: #fff; padding: clamp(60px, 9vw, 120px) clamp(16px, 4vw, 40px); }
.lkn-section__head--light h2 { color: #fff; font-size: clamp(28px, 4vw, 46px); }
.lkn-steps__list { list-style: none; padding: 0; margin: 0 auto; max-width: var(--maxw); display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; counter-reset: step; }
.lkn-step { background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: var(--radius); padding: 30px; position: relative; }
.lkn-step__n { font-family: var(--display); font-weight: 800; font-size: 22px; width: 48px; height: 48px; display: grid; place-items: center; border-radius: 14px; background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; margin-bottom: 18px; }
.lkn-step h3 { font-size: 21px; margin-bottom: 8px; }
.lkn-step p { color: rgba(255,255,255,.72); }
.lkn-steps__cta { text-align: center; margin-top: 44px; }

/* ── About ──────────────────────────────────────────────── */
.lkn-about { background: linear-gradient(180deg, #fff, var(--soft)); padding: clamp(56px, 8vw, 110px) clamp(16px, 4vw, 40px); }
.lkn-about__inner { max-width: 760px; margin: 0 auto; text-align: center; }
.lkn-about h2 { font-size: clamp(26px, 3.8vw, 42px); margin-bottom: 22px; }
.lkn-about p { color: var(--muted); font-size: 18px; margin-bottom: 18px; }
.lkn-about em { color: var(--ink); font-style: italic; }

/* ── Parents ────────────────────────────────────────────── */
.lkn-parents { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.lkn-parent { background: #fff; border: 1px solid var(--line); border-radius: var(--radius); padding: 28px 24px; transition: transform .2s ease, box-shadow .2s ease; }
.lkn-parent:hover { transform: translateY(-4px); box-shadow: 0 22px 40px -26px rgba(15,31,46,.4); }
.lkn-parent__icon { display: grid; place-items: center; width: 50px; height: 50px; border-radius: 14px; background: rgba(31,168,242,.12); color: var(--blue); margin-bottom: 16px; }
.lkn-parent__icon svg { width: 26px; height: 26px; }
.lkn-parent__icon[data-icon="shield"] { background: rgba(59,196,181,.16); color: var(--teal-d); }
.lkn-parent__icon[data-icon="gift"] { background: rgba(245,158,11,.16); color: var(--amber); }
.lkn-parent__icon[data-icon="flag"] { background: rgba(139,92,246,.14); color: var(--purple); }
.lkn-parent h3 { font-size: 19px; margin-bottom: 8px; }
.lkn-parent p { color: var(--muted); font-size: 15px; }

/* ── CTA band ───────────────────────────────────────────── */
.lkn-cta { padding: clamp(20px, 4vw, 48px) clamp(16px, 4vw, 40px) clamp(40px, 6vw, 80px); }
.lkn-cta__inner {
  max-width: var(--maxw); margin: 0 auto; text-align: center; color: #fff;
  border-radius: 34px; padding: clamp(44px, 6vw, 80px) clamp(20px, 4vw, 60px);
  background: linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%); position: relative; overflow: hidden;
}
.lkn-cta__inner::before { content: ''; position: absolute; inset: 0; background: radial-gradient(120% 140% at 85% -20%, rgba(255,255,255,.28), transparent 50%); }
.lkn-cta__lock { position: relative; width: 56px; height: 56px; color: #fff; margin-bottom: 14px; }
.lkn-cta h2 { position: relative; font-size: clamp(26px, 4vw, 44px); }
.lkn-cta p { position: relative; margin: 14px auto 0; max-width: 36em; font-size: 18px; color: rgba(255,255,255,.92); }
.lkn-cta__actions { position: relative; margin-top: 30px; display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; }

/* ── Footer ─────────────────────────────────────────────── */
.lkn-footer { background: var(--ink); color: rgba(255,255,255,.7); padding: clamp(48px, 6vw, 72px) clamp(16px, 4vw, 40px) 30px; }
.lkn-footer__top { max-width: var(--maxw); margin: 0 auto; display: flex; flex-wrap: wrap; gap: 40px; justify-content: space-between; padding-bottom: 36px; border-bottom: 1px solid rgba(255,255,255,.1); }
.lkn-footer__brand p { margin-top: 14px; font-size: 14px; max-width: 24em; }
.lkn-footer__cols { display: flex; gap: 64px; }
.lkn-footer__cols h4 { font-family: var(--display); color: #fff; font-size: 14px; letter-spacing: .04em; margin-bottom: 14px; }
.lkn-footer__cols div { display: flex; flex-direction: column; gap: 10px; }
.lkn-footer__cols a { font-size: 14px; transition: color .2s; }
.lkn-footer__cols a:hover { color: #fff; }
.lkn-footer__bottom { max-width: var(--maxw); margin: 20px auto 0; display: flex; flex-wrap: wrap; gap: 8px 24px; justify-content: space-between; font-size: 13px; color: rgba(255,255,255,.5); }

/* ── Motion ─────────────────────────────────────────────── */
@keyframes underline { to { transform: scaleX(1); } }
@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }

/* Staggered hero entrance */
.lkn-eyebrow, .lkn-hero__title .r1, .lkn-hero__title .r2, .lkn-hero__title .r3,
.lkn-hero__lead, .lkn-hero__actions, .lkn-trust, .lkn-hero__visual {
  animation: rise .7s cubic-bezier(.2,.7,.2,1) both;
}
.lkn-hero__title .r1 { animation-delay: .06s; }
.lkn-hero__title .r2 { animation-delay: .14s; }
.lkn-hero__title .r3 { animation-delay: .22s; }
.lkn-hero__lead { animation-delay: .30s; }
.lkn-hero__actions { animation-delay: .38s; }
.lkn-trust { animation-delay: .46s; }
.lkn-hero__visual { animation-delay: .30s; }
@keyframes rise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }

/* Gentle scroll-driven lift for section content WHERE SUPPORTED. Important:
   this only nudges transform — never opacity — so content is always visible
   (in the prerendered HTML Google sees, in full-page captures, and in
   browsers without scroll-timeline support). Decorative, never load-bearing. */
@supports (animation-timeline: view()) {
  @media (prefers-reduced-motion: no-preference) {
    .lkn-feature, .lkn-step, .lkn-parent, .lkn-cta__inner {
      animation: rise-scroll linear both;
      animation-timeline: view();
      animation-range: entry 2% cover 22%;
    }
  }
}
@keyframes rise-scroll { from { transform: translateY(34px); } to { transform: none; } }

@media (prefers-reduced-motion: reduce) {
  .lkn *, .lkn *::before, .lkn *::after { animation: none !important; transition: none !important; }
  .lkn-hero__title em::after { transform: scaleX(1); }
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 940px) {
  .lkn-hero { grid-template-columns: 1fr; text-align: center; padding-top: clamp(28px, 8vw, 56px); }
  .lkn-hero__lead, .lkn-trust { margin-inline: auto; justify-content: center; }
  .lkn-hero__actions { justify-content: center; }
  .lkn-hero__visual { margin-top: 18px; }
  .lkn-feature, .lkn-feature.is-flip { grid-template-columns: 1fr; text-align: center; }
  .lkn-feature.is-flip .lkn-feature__text { order: 0; }
  .lkn-feature__text p { margin-inline: auto; }
  .lkn-checks { justify-items: center; }
  .lkn-checks li { justify-content: center; }
  .lkn-steps__list { grid-template-columns: 1fr; }
  .lkn-parents { grid-template-columns: repeat(2, 1fr); }
  .lkn-nav { display: none; }
}
@media (max-width: 540px) {
  .lkn-header { gap: 10px; padding: 11px 16px; }
  .lkn-header__cta .lkn-btn--ghost { display: none; }
  .lkn-parents { grid-template-columns: 1fr; }
  .lkn-footer__top { flex-direction: column; gap: 28px; }
  .lkn-footer__cols { gap: 40px; }
  .lkn-chip--points { left: 0; }
  .lkn-chip--streak { right: 0; }
}
</style>
